import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Puzzle, Download, Trash2, Settings } from 'lucide-react';

interface Extension {
    id: number;
    slug: string;
    name: string;
    description: string | null;
    type: string;
    category: string;
    icon_url: string | null;
    is_featured: boolean;
    install_count: number;
    is_installed?: boolean;
}

interface UserExtension {
    id: number;
    extension_id: number;
    config: Record<string, any> | null;
    is_active: boolean;
    extension: Extension;
}

const CATEGORY_LABELS: Record<string, string> = {
    contract: 'Contract',
    profile: 'Profile',
    payment: 'Payment',
    notification: 'Notification',
    widget: 'Widget',
};

export default function ExtensionsPage() {
    const [tab, setTab] = useState<'browse' | 'installed'>('browse');
    const [extensions, setExtensions] = useState<Extension[]>([]);
    const [installed, setInstalled] = useState<UserExtension[]>([]);
    const [category, setCategory] = useState('');

    const fetchExtensions = () => {
        const params = category ? `?category=${category}` : '';
        api.get<{ data: Extension[] }>(`/extensions${params}`).then((r) => setExtensions(r.data));
    };

    const fetchInstalled = () => {
        api.get<{ data: UserExtension[] }>('/extensions/mine').then((r) => setInstalled(r.data));
    };

    useEffect(() => {
        fetchExtensions();
        fetchInstalled();
    }, [category]);

    const handleInstall = async (id: number) => {
        await api.post(`/extensions/${id}/install`);
        fetchExtensions();
        fetchInstalled();
    };

    const handleUninstall = async (id: number) => {
        await api.delete(`/extensions/${id}/install`);
        fetchExtensions();
        fetchInstalled();
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-semibold">Extensions</h1>
                    <p className="text-sm text-slate-400">Enhance your DebtBound experience</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 rounded-lg bg-slate-800/50 p-1">
                <button
                    onClick={() => setTab('browse')}
                    className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition ${
                        tab === 'browse' ? 'bg-violet-600 text-white' : 'text-slate-400 hover:text-slate-200'
                    }`}
                >
                    Browse
                </button>
                <button
                    onClick={() => setTab('installed')}
                    className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition ${
                        tab === 'installed' ? 'bg-violet-600 text-white' : 'text-slate-400 hover:text-slate-200'
                    }`}
                >
                    Installed ({installed.length})
                </button>
            </div>

            {tab === 'browse' && (
                <>
                    {/* Category filter */}
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={() => setCategory('')}
                            className={`rounded-full px-3 py-1 text-xs transition ${
                                !category ? 'bg-violet-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                            }`}
                        >
                            All
                        </button>
                        {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                            <button
                                key={key}
                                onClick={() => setCategory(key)}
                                className={`rounded-full px-3 py-1 text-xs transition ${
                                    category === key ? 'bg-violet-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    {extensions.length === 0 ? (
                        <Card>
                            <CardContent className="py-8 text-center">
                                <Puzzle className="mx-auto mb-3 h-10 w-10 text-slate-600" />
                                <p className="text-sm text-slate-500">No extensions available yet.</p>
                                <p className="mt-1 text-xs text-slate-600">Check back later or build your own!</p>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="grid gap-4 sm:grid-cols-2">
                            {extensions.map((ext) => (
                                <Card key={ext.id}>
                                    <CardContent className="p-4">
                                        <div className="flex items-start gap-3">
                                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-violet-900/50">
                                                {ext.icon_url ? (
                                                    <img src={ext.icon_url} alt="" className="h-6 w-6 rounded" />
                                                ) : (
                                                    <Puzzle className="h-5 w-5 text-violet-400" />
                                                )}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2">
                                                    <h3 className="text-sm font-medium">{ext.name}</h3>
                                                    {ext.is_featured && (
                                                        <Badge className="text-xs bg-amber-600">Featured</Badge>
                                                    )}
                                                </div>
                                                <p className="mt-0.5 text-xs text-slate-400 line-clamp-2">
                                                    {ext.description || 'No description'}
                                                </p>
                                                <div className="mt-2 flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        <Badge variant="outline" className="text-xs">
                                                            {CATEGORY_LABELS[ext.category] ?? ext.category}
                                                        </Badge>
                                                        <span className="text-xs text-slate-500">
                                                            {ext.install_count} installs
                                                        </span>
                                                    </div>
                                                    {ext.is_installed ? (
                                                        <Button variant="outline" size="sm" onClick={() => handleUninstall(ext.id)}>
                                                            <Trash2 className="mr-1 h-3 w-3" />
                                                            Remove
                                                        </Button>
                                                    ) : (
                                                        <Button size="sm" onClick={() => handleInstall(ext.id)}>
                                                            <Download className="mr-1 h-3 w-3" />
                                                            Install
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </>
            )}

            {tab === 'installed' && (
                <>
                    {installed.length === 0 ? (
                        <Card>
                            <CardContent className="py-8 text-center">
                                <p className="text-sm text-slate-500">
                                    No extensions installed. Browse the marketplace to find some!
                                </p>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="space-y-3">
                            {installed.map((ue) => (
                                <Card key={ue.id}>
                                    <CardContent className="flex items-center justify-between p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-900/50">
                                                <Puzzle className="h-4 w-4 text-violet-400" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium">{ue.extension.name}</p>
                                                <p className="text-xs text-slate-500">
                                                    {ue.is_active ? 'Active' : 'Disabled'}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex gap-1">
                                            {ue.extension.config_schema && (
                                                <Button variant="ghost" size="sm">
                                                    <Settings className="h-4 w-4" />
                                                </Button>
                                            )}
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleUninstall(ue.extension.id)}
                                                className="text-red-400 hover:text-red-300"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
